/**
 * Scope model for the metrics page: the filter shape, the relative date presets, and
 * the bucket-cap rule. The controls that edit it live in MetricsToolbar.
 *
 * One scope drives every card. Per-chart filters would let two cards disagree about
 * what period they show, so the toolbar sits above all of them and each chart
 * re-renders against this single slice.
 *
 * State is URL-synced so a view can be shared as a link and survives reload - the old
 * page kept filter state purely local, so a refresh silently reset it to a hardcoded
 * 2024 range.
 */

import type { Grain } from "../../services/sales-metrics";
import { toLocalDateString } from "../../services/sales-metrics";

export interface MetricsFilter {
  start: string; // YYYY-MM-DD, Europe/Berlin
  end: string;
  grain: Grain;
}

export interface DatePreset {
  id: string;
  label: string;
  resolve: () => { start: string; end: string; grain: Grain };
}

/** "Today" in Berlin, so presets line up with how the RPCs bucket their dates. */
function berlinToday(): Date {
  return new Date(toLocalDateString(new Date()) + "T12:00:00");
}

function shiftDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

export const DATE_PRESETS: DatePreset[] = [
  {
    id: "last7",
    label: "Letzte 7 Tage",
    resolve: () => {
      const today = berlinToday();
      return {
        start: toLocalDateString(shiftDays(today, -6)),
        end: toLocalDateString(today),
        grain: "auto",
      };
    },
  },
  {
    id: "last30",
    label: "Letzte 30 Tage",
    resolve: () => {
      const today = berlinToday();
      return {
        start: toLocalDateString(shiftDays(today, -29)),
        end: toLocalDateString(today),
        grain: "auto",
      };
    },
  },
  {
    id: "mtd",
    label: "Aktueller Monat",
    resolve: () => {
      const today = berlinToday();
      const first = new Date(today.getFullYear(), today.getMonth(), 1, 12);
      return {
        start: toLocalDateString(first),
        end: toLocalDateString(today),
        grain: "auto",
      };
    },
  },
  {
    id: "lastMonth",
    label: "Letzter Monat",
    resolve: () => {
      const today = berlinToday();
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1, 12);
      const last = new Date(today.getFullYear(), today.getMonth(), 0, 12);
      return {
        start: toLocalDateString(first),
        end: toLocalDateString(last),
        grain: "auto",
      };
    },
  },
  {
    id: "last90",
    label: "Letzte 90 Tage",
    resolve: () => {
      const today = berlinToday();
      return {
        start: toLocalDateString(shiftDays(today, -89)),
        end: toLocalDateString(today),
        grain: "auto",
      };
    },
  },
  {
    id: "last12m",
    label: "Letzte 12 Monate",
    resolve: () => {
      const today = berlinToday();
      const start = new Date(today.getFullYear() - 1, today.getMonth(), 1, 12);
      return {
        start: toLocalDateString(start),
        end: toLocalDateString(today),
        grain: "auto",
      };
    },
  },
];

/** Default view: current month to date. Never a hardcoded historical range. */
export function defaultFilter(): MetricsFilter {
  const preset = DATE_PRESETS.find((p) => p.id === "mtd") ?? DATE_PRESETS[0]!;
  return preset.resolve();
}

/**
 * Buckets an explicit grain would produce for a range. Mirrors the server-side bound in
 * get_metrics_timeseries: past MAX_BUCKETS the RPC refuses rather than truncating, so
 * the UI must not offer a grain that would fail.
 */
const MAX_BUCKETS = 800;

function bucketCount(start: string, end: string, grain: Exclude<Grain, "auto">): number {
  const days =
    Math.round(
      (new Date(`${end}T00:00:00`).getTime() - new Date(`${start}T00:00:00`).getTime()) /
        86_400_000
    ) + 1;
  if (grain === "day") return days;
  if (grain === "week") return Math.ceil(days / 7) + 1;
  return Math.ceil(days / 28) + 1;
}

export function grainFits(
  start: string,
  end: string,
  grain: Grain
): boolean {
  if (grain === "auto") return true;
  return bucketCount(start, end, grain) <= MAX_BUCKETS;
}
